/**
 * Technicians view — Phase 2.3.i.
 *
 * Owns the Technicians page: technician table + board mode toggle, the
 * technician add/edit dialog, the per-technician status dialog
 * (assigning open tickets via drag-and-drop), and the
 * tech-resolution-trend chart shown alongside the table.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `hasRole` — generic.
 *   - `formatRelativeTime`, `formatDateTimeShort` — formatters.
 *   - `parseListInput` — utility.
 *   - `getTicketsBoardEl` — admin.js owns the tickets-board DOM ref.
 *   - `ensureTechnicianUsersLoaded`, `ensureTechnicianRosterLoaded` —
 *     admin.js owns the global admin-user roster cache and its
 *     "ensure loaded" guards. The technicians module is a consumer.
 *   - `getTechnicianAdminUsers`, `getCurrentUserId` — read-only access
 *     to the admin-users cache for option-list rendering.
 *   - `getTechnicianRosterState` / `setTechnicianRosterState` — same
 *     pattern HR + crm-installations use.
 *   - Tickets-board interop: this module calls `adminViews.tickets.
 *     technicianBoard.{populateSupervisorFilter, isInitialized, isStale,
 *     refresh, focusLane}` directly via `window.adminViews`.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from `src/lib`. Roster state + ensure* loaders come from
 * `src/lib/admin-roster.js`. The current admin user id comes from
 * `getCurrentAdminUser` in `src/lib/roles.js`.
 */

import { escapeHtml, formatRelativeTime, formatDateTimeShort, formatDateTimeFull, parseListInput, formatLocalDayKey, formatResolutionDayLabel } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole, getCurrentAdminUser } from '../../src/lib/roles.js';
import { renderCustomerMap, customerMapExportUrl } from '../lib/customer-map.js';
import { formatTicketStatus, extractRoleNames, adminUserSortKey, formatAssignmentOptionLabel, renderBoardQueuePanel } from '../../src/lib/ticket-utils.js';
import {
  ensureTechnicianUsersLoaded, ensureTechnicianRosterLoaded,
  ensureSupervisorUsersLoaded,
  getTechnicianAdminUsers, getTechnicianRosterState, setTechnicianRosterState,
  updateTechnicianProfiles,
} from '../../src/lib/admin-roster.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  const getTicketsBoardEl = () => document.getElementById('ticketsBoard');
  const getCurrentUserId = () => {
    const me = getCurrentAdminUser();
    return me && me.id != null ? String(me.id) : null;
  };

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let techniciansTbody, technicianDlg, technicianDlgTitle, technicianForm,
      technicianAdminUserSelect, technicianDisplayNameInput, technicianPhoneInput,
      technicianSkillsInput, technicianRegionsInput, technicianActiveInput,
      technicianSubmitBtn, addTechnicianBtn,
      technicianStatusDlg, technicianStatusForm, technicianStatusReason,
      technicianStatusName, technicianStatusSelect, technicianStatusTickets,
      technicianStatusTechList, technicianStatusEmpty, technicianStatusSubmit,
      technicianStatusCancel, technicianStatusClose, techniciansView,
      techModeBoardBtn, techModeTableBtn, techModeMapBtn, techniciansTableWrap,
      techCustomerMapWrap, techCustomerMapDiv, techCustomerMapMeta, techCustomerMapExport,
      ticketsBoard, techResolutionTrend, techResolutionStrip, techResolutionLegend,
      techResolutionTrendNote, techOutputGrid, techOutputLegend, techOutputTrendNote, techOutputStrip;
  const technicianStatusState = {
    technician: null,
    tickets: [],
    assignments: {},
    loadingTickets: false,
  };

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    techniciansTbody = document.getElementById('techniciansTbody');
    technicianDlg = document.getElementById('technicianDlg');
    technicianDlgTitle = document.getElementById('technicianDlgTitle');
    technicianForm = document.getElementById('technicianForm');
    technicianAdminUserSelect = document.getElementById('technicianAdminUser');
    technicianDisplayNameInput = document.getElementById('technicianDisplayName');
    technicianPhoneInput = document.getElementById('technicianPhone');
    technicianSkillsInput = document.getElementById('technicianSkills');
    technicianRegionsInput = document.getElementById('technicianRegions');
    technicianActiveInput = document.getElementById('technicianActive');
    technicianSubmitBtn = document.getElementById('technicianSubmitBtn');
    addTechnicianBtn = document.getElementById('addTechnicianBtn');
    technicianStatusDlg = document.getElementById('technicianStatusDlg');
    technicianStatusForm = document.getElementById('technicianStatusForm');
    technicianStatusReason = document.getElementById('technicianStatusReason');
    technicianStatusName = document.getElementById('technicianStatusName');
    technicianStatusSelect = document.getElementById('technicianStatusSelect');
    technicianStatusTickets = document.getElementById('technicianStatusTickets');
    technicianStatusTechList = document.getElementById('technicianStatusTechList');
    technicianStatusEmpty = document.getElementById('technicianStatusEmpty');
    technicianStatusSubmit = document.getElementById('technicianStatusSubmit');
    technicianStatusCancel = document.getElementById('technicianStatusCancel');
    technicianStatusClose = document.getElementById('technicianStatusClose');
    techniciansView = document.getElementById('techniciansView');
    techModeBoardBtn = document.getElementById('techModeBoardBtn');
    techModeTableBtn = document.getElementById('techModeTableBtn');
    techModeMapBtn = document.getElementById('techModeMapBtn');
    techniciansTableWrap = document.getElementById('techniciansTableWrap');
    techCustomerMapWrap = document.getElementById('techCustomerMapWrap');
    techCustomerMapDiv = document.getElementById('techCustomerMap');
    techCustomerMapMeta = document.getElementById('techCustomerMapMeta');
    techCustomerMapExport = document.getElementById('techCustomerMapExport');
    ticketsBoard = getTicketsBoardEl();
    techResolutionTrend = document.getElementById('techResolutionTrend');
    techResolutionStrip = techResolutionTrend ? techResolutionTrend.closest('.tech-resolution-strip') : null;
    techResolutionLegend = document.getElementById('techResolutionLegend');
    techResolutionTrendNote = document.getElementById('techResolutionTrendNote');
    techOutputGrid = document.getElementById('techOutputGrid');
    techOutputLegend = document.getElementById('techOutputLegend');
    techOutputTrendNote = document.getElementById('techOutputTrendNote');
    techOutputStrip = document.getElementById('techOutputStrip');
  }

  // ---- State (verbatim from admin.js) ---------------------------------
  let techniciansInitialized = false;
  let techniciansViewMode = 'board';
  const techniciansState = {
    items: [],
    loading: false,
  };
  let technicianEditId = null;
  // Resolution-trend chart state. Was left behind in admin.js during the
  // Phase 3 extraction — reading it via the absent binding threw
  // ReferenceError on every call (line ~212), the `.catch(() => {})` at
  // the call sites swallowed it, and the chart never rendered.
  const techResolutionTrendState = {
    loading: false,
    items: [],
    lastLoadedAt: null,
    error: null,
  };

  // ---- Region 1 (verbatim lines 3511-3714): tech helpers + chart + view-mode
    function buildTechnicianInitials(label) {
      const raw = String(label || '').trim();
      if (!raw) return '?';
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    function hashTechColor(key) {
      const str = String(key || '');
      let hash = 0;
      for (let i = 0; i < str.length; i += 1) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
      }
      const hue = Math.abs(hash) % 360;
      return `hsl(${hue}, 62%, 48%)`;
    }

    function renderTechResolutionTrend(series = [], techs = []) {
      if (!techResolutionTrend) return;
      techResolutionTrend.innerHTML = '';
      if (techResolutionLegend) techResolutionLegend.innerHTML = '';
      if (!Array.isArray(series) || series.length === 0) {
        techResolutionTrend.innerHTML = '<div style="color:var(--sub); font-size:12px;">No data</div>';
        return;
      }
      const allCounts = [];
      series.forEach((entry) => {
        (entry.breakdown || []).forEach((row) => {
          allCounts.push(Number(row.count || 0));
        });
      });
      const maxCount = Math.max(...allCounts, 1);
      if (techResolutionLegend && Array.isArray(techs) && techs.length) {
        techs.forEach((tech) => {
          const item = document.createElement('div');
          item.className = 'tech-resolution-legend-item';
          const swatch = document.createElement('span');
          swatch.className = 'tech-resolution-legend-swatch';
          swatch.style.background = tech.color;
          const label = document.createElement('span');
          label.textContent = `${tech.initials} ${tech.label}`;
          item.appendChild(swatch);
          item.appendChild(label);
          techResolutionLegend.appendChild(item);
        });
      }
      series.forEach((entry) => {
        const label = formatResolutionDayLabel(entry.day);
        const dayEl = document.createElement('div');
        dayEl.className = 'tech-resolution-day';
        const bars = document.createElement('div');
        bars.className = 'tech-resolution-day-bars';
        const breakdownMap = new Map();
        (entry.breakdown || []).forEach((row) => {
          if (row && row.key) breakdownMap.set(row.key, row);
        });
        techs.forEach((tech) => {
          const row = breakdownMap.get(tech.key);
          const count = row ? Number(row.count || 0) : 0;
          const heightPct = maxCount ? Math.round((count / maxCount) * 100) : 0;
          const techEl = document.createElement('div');
          techEl.className = 'tech-resolution-tech';
          const countEl = document.createElement('div');
          countEl.className = 'tech-resolution-tech-count';
          countEl.textContent = String(count);
          const bar = document.createElement('div');
          bar.className = 'tech-resolution-tech-bar';
          bar.style.height = `${Math.max(count ? 8 : 3, Math.round(56 * heightPct / 100))}px`;
          bar.style.background = tech.color;
          bar.title = `${entry.day}: ${tech.label} (${count})`;
          const initial = document.createElement('div');
          initial.className = 'tech-resolution-tech-initial';
          initial.textContent = tech.initials;
          techEl.appendChild(countEl);
          techEl.appendChild(bar);
          techEl.appendChild(initial);
          bars.appendChild(techEl);
        });
        const dayLabel = document.createElement('div');
        dayLabel.className = 'tech-resolution-label';
        const dayTotal = Number(entry.total || 0);
        dayLabel.textContent = label ? `${label} · ${dayTotal}` : String(dayTotal);
        dayEl.appendChild(bars);
        dayEl.appendChild(dayLabel);
        techResolutionTrend.appendChild(dayEl);
      });
    }

    async function loadTechnicianQueuePanel() {
      const panel = document.getElementById('technicianQueuePanel');
      if (!panel) return;
      try {
        const data = await adminAPI.fetch(API.adminTechnicianQueueSummary, { method: 'GET' });
        renderBoardQueuePanel(panel, (data && data.rows) || [], { icon: '🧑‍🔧', title: 'Technician queues' });
      } catch (err) {
        panel.innerHTML = '<div class="muted" style="font-size:12px;">Queue summary unavailable.</div>';
        console.error('loadTechnicianQueuePanel failed:', err);
      }
    }

    async function loadTechResolutionTrend() {
      if (!techResolutionTrend || techResolutionTrendState.loading) return;
      if (!hasRole('field_tech_supervisor', 'customer_care', 'customer_care_supervisor', 'root')) {
        if (techResolutionStrip) techResolutionStrip.style.display = 'none';
        return;
      }
      if (techResolutionStrip) techResolutionStrip.style.display = '';
      techResolutionTrendState.loading = true;
      if (techResolutionTrendNote) techResolutionTrendNote.textContent = 'Loading…';
      techResolutionTrend.innerHTML = '<div style="color:var(--sub); font-size:12px;">Loading…</div>';
      try {
        const now = new Date();
        const days = [];
        for (let offset = 4; offset >= 0; offset -= 1) {
          const day = new Date(now);
          day.setDate(now.getDate() - offset);
          days.push(day);
        }
        const dayKeys = days.map((day) => formatLocalDayKey(day));
        const requests = dayKeys.map((day) => adminAPI.fetch(`${API.adminDailyStatus}?day=${encodeURIComponent(day)}`, { method: 'GET' }));
        const results = await Promise.allSettled(requests);
        const techTotals = new Map();
        const daySeries = dayKeys.map((day, idx) => {
          const res = results[idx];
          if (res.status === 'fulfilled') {
            const entries = Array.isArray(res.value?.technicians) ? res.value.technicians : [];
            const breakdown = [];
            entries.forEach((tech) => {
              const completed = Number(tech?.summary?.completed || 0);
              if (!completed) return;
              const label = tech?.display_name || `Tech ${tech?.admin_user_id || ''}`.trim();
              const initials = buildTechnicianInitials(label);
              const key = String(tech?.admin_user_id || tech?.technician_id || label || initials);
              const existing = techTotals.get(key) || { key, label, initials, total: 0 };
              existing.total += completed;
              techTotals.set(key, existing);
              breakdown.push({ key, label, initials, count: completed });
            });
            const total = breakdown.reduce((acc, row) => acc + row.count, 0);
            return { day, total, breakdown };
          }
          return { day, total: 0, breakdown: [] };
        });
        const techList = Array.from(techTotals.values())
          .sort((a, b) => b.total - a.total)
          .map((item) => ({
            ...item,
            color: hashTechColor(item.key),
          }));
        const series = daySeries.map((entry) => {
          const breakdown = entry.breakdown
            .map((row) => {
              const tech = techList.find((item) => item.key === row.key);
              return {
                ...row,
                color: tech ? tech.color : hashTechColor(row.key),
              };
            })
            .sort((a, b) => b.count - a.count);
          return { ...entry, breakdown };
        });
        techResolutionTrendState.items = series;
        techResolutionTrendState.error = null;
        techResolutionTrendState.lastLoadedAt = new Date().toISOString();
        renderTechResolutionTrend(series, techList);
        loadTechnicianQueuePanel();   // queue panel beside the chart (fire-and-forget)
        if (techResolutionTrendNote) techResolutionTrendNote.textContent = 'Updated just now';
      } catch (err) {
        techResolutionTrendState.error = err;
        techResolutionTrend.innerHTML = '<div style="color:var(--err); font-size:12px;">Failed to load trend</div>';
        if (techResolutionTrendNote) techResolutionTrendNote.textContent = '';
        // Don't swallow the diagnostic — surface it in DevTools (and to
        // the smoke-test navigation-coverage check) so the next missing
        // import / undeclared helper isn't hidden behind the generic
        // "Failed to load trend" banner.
        // eslint-disable-next-line no-console
        console.error('loadTechResolutionTrend failed:', err);
      } finally {
        techResolutionTrendState.loading = false;
      }
    }

    // ---- Weekly output & quality (small multiples) ----------------------
    const techOutputTrendState = { loading: false, loaded: false };

    // A tech's two weekly lines drawn as one inline-SVG spark: gray = total
    // resolutions, teal = the share scoring >= the high-quality threshold. The
    // gap between them is low/unscored work. Shared y-scale across all techs so
    // taller genuinely means more output.
    function _outputSpark(total, high, gmax) {
      const W = 200, H = 40, P = 3, n = total.length;
      if (!n || gmax <= 0) return '';
      const x = (i) => P + (i * (W - 2 * P)) / Math.max(1, n - 1);
      const y = (v) => H - P - (Math.max(0, v) / gmax) * (H - 2 * P);
      const line = (a) => a.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
      const area = (a) => `${line(a)} L${x(n - 1).toFixed(1)} ${(H - P).toFixed(1)} L${x(0).toFixed(1)} ${(H - P).toFixed(1)} Z`;
      return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="tech-output-svg" aria-hidden="true">
        <path d="${area(total)}" class="tech-output-area-total"/>
        <path d="${area(high)}" class="tech-output-area-high"/>
        <path d="${line(total)}" class="tech-output-line-total" fill="none" vector-effect="non-scaling-stroke"/>
        <path d="${line(high)}" class="tech-output-line-high" fill="none" vector-effect="non-scaling-stroke"/>
        <circle cx="${x(n - 1).toFixed(1)}" cy="${y(total[n - 1]).toFixed(1)}" r="2.4" class="tech-output-dot-total"/>
        <circle cx="${x(n - 1).toFixed(1)}" cy="${y(high[n - 1]).toFixed(1)}" r="2.6" class="tech-output-dot-high"/>
      </svg>`;
    }

    function _qualityClassFor(pct) {
      return pct >= 70 ? 'hi' : pct >= 55 ? 'mid' : 'lo';
    }

    function renderTechOutputTrend(payload) {
      if (!techOutputGrid) return;
      const techs = Array.isArray(payload && payload.technicians) ? payload.technicians : [];
      const threshold = payload && payload.high_quality_threshold != null ? payload.high_quality_threshold : 60;
      const months = (payload && payload.months) || [];

      if (techOutputLegend) {
        techOutputLegend.innerHTML = `
          <span class="tech-output-lg"><span class="tech-output-sw total"></span>Total resolutions</span>
          <span class="tech-output-lg"><span class="tech-output-sw high"></span>High quality (≥${escapeHtml(String(Math.round(threshold)))}%)</span>
          <span class="tech-output-chip">${techs.length} tech · 13 weeks</span>`;
      }

      if (!techs.length) {
        techOutputGrid.innerHTML = '<div class="tech-output-empty">No resolutions recorded in the last 3 months.</div>';
        return;
      }

      const gmax = Math.max(1, ...techs.flatMap((t) => t.weekly_total || [])) * 1.08;
      techOutputGrid.innerHTML = techs.map((t) => {
        const monthCells = (t.monthly || []).map((m) => `
          <tr>
            <td class="tech-output-mo">${escapeHtml(m.label || '')}</td>
            <td>${escapeHtml(String(m.total ?? 0))}</td>
            <td class="tech-output-hqv">${escapeHtml(String(m.high_quality ?? 0))}</td>
            <td class="tech-output-q ${_qualityClassFor(m.quality_pct || 0)}">${escapeHtml(String(m.quality_pct ?? 0))}%</td>
          </tr>`).join('');
        return `
          <div class="tech-output-cell">
            <div class="tech-output-who">
              <div class="tech-output-name" title="${escapeHtml(t.name || '')}">${escapeHtml(t.name || '')}</div>
              <div class="tech-output-sub">${escapeHtml(String(t.season_total ?? 0))} resolved · ${escapeHtml(String(t.season_quality_pct ?? 0))}% high quality</div>
              <div class="tech-output-spark">${_outputSpark(t.weekly_total || [], t.weekly_high_quality || [], gmax)}</div>
            </div>
            <table class="tech-output-mt">
              <thead><tr><th>Mo</th><th>Out</th><th>≥${escapeHtml(String(Math.round(threshold)))}</th><th>Q</th></tr></thead>
              <tbody>${monthCells}</tbody>
            </table>
          </div>`;
      }).join('');
    }

    async function loadTechOutputTrend() {
      if (!techOutputGrid || techOutputTrendState.loading) return;
      techOutputTrendState.loading = true;
      if (!techOutputTrendState.loaded) {
        techOutputGrid.innerHTML = '<div class="tech-output-empty">Loading…</div>';
      }
      try {
        const payload = await adminAPI.fetch(API.adminTechnicianOutputTrend, { method: 'GET' });
        renderTechOutputTrend(payload || {});
        techOutputTrendState.loaded = true;
        if (techOutputTrendNote) techOutputTrendNote.textContent = 'Updated just now';
      } catch (err) {
        techOutputGrid.innerHTML = '<div class="tech-output-empty" style="color:var(--err);">Failed to load output trend</div>';
        // eslint-disable-next-line no-console
        console.error('loadTechOutputTrend failed:', err);
      } finally {
        techOutputTrendState.loading = false;
      }
    }

    function setTechniciansViewMode(mode, options = {}) {
      const { focusTechnicianId } = options;
      const resolved = ['table', 'map'].includes(mode) ? mode : 'board';
      techniciansViewMode = resolved;
      const setToggle = (btn, on) => {
        if (!btn) return;
        btn.classList.toggle('active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      };
      setToggle(techModeBoardBtn, resolved === 'board');
      setToggle(techModeTableBtn, resolved === 'table');
      setToggle(techModeMapBtn, resolved === 'map');
      if (techniciansTableWrap) {
        techniciansTableWrap.style.display = resolved === 'table' ? '' : 'none';
      }
      if (ticketsBoard) {
        ticketsBoard.hidden = resolved !== 'board';
        ticketsBoard.style.display = resolved === 'board' ? '' : 'none';
      }
      if (techCustomerMapWrap) {
        techCustomerMapWrap.style.display = resolved === 'map' ? '' : 'none';
      }
      // The customer map replaces the whole Tech Support surface (charts included).
      if (techResolutionStrip) techResolutionStrip.style.display = resolved === 'map' ? 'none' : '';
      if (techOutputStrip) techOutputStrip.style.display = resolved === 'map' ? 'none' : '';

      if (resolved === 'map') {
        renderCustomerMap({ container: techCustomerMapDiv, metaEl: techCustomerMapMeta }).catch(() => {});
        return;
      }

      if (resolved === 'table') {
        loadTechnicians().catch(() => {});
        loadTechResolutionTrend().catch(() => {});
        loadTechOutputTrend().catch(() => {});
        return;
      }

      loadTechResolutionTrend().catch(() => {});
      loadTechOutputTrend().catch(() => {});
      const tb = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
      ensureSupervisorUsersLoaded().then(() => {
        if (tb) tb.populateSupervisorFilter();
      }).catch(() => {});
      const shouldLoadBoard = !tb || !tb.isInitialized() || tb.isStale();
      const boardPromise = (shouldLoadBoard && tb) ? tb.refresh() : Promise.resolve();
      if (focusTechnicianId != null && tb) {
        boardPromise
          .then(() => tb.focusLane(focusTechnicianId))
          .catch(() => {});
      }
    }

  // ---- Region 2 (verbatim lines 5326-5371): form helpers
    function userHasTechnicianRole(user) {
      const roles = extractRoleNames(user);
      return roles.includes('field_tech') || roles.includes('field_tech_supervisor');
    }

    function populateTechnicianAdminUserOptions(selectedId = null) {
      if (!technicianAdminUserSelect) return;
      const previous = technicianAdminUserSelect.value;
      technicianAdminUserSelect.innerHTML = '<option value="" disabled selected>Select admin user…</option>';
      const allUsers = getTechnicianAdminUsers();
      const eligibleUsers = allUsers
        .filter(userHasTechnicianRole)
        .sort((a, b) => adminUserSortKey(a).localeCompare(adminUserSortKey(b)));
      eligibleUsers.forEach((user) => {
        const option = document.createElement('option');
        option.value = String(user.id);
        option.textContent = formatAssignmentOptionLabel(user);
        technicianAdminUserSelect.appendChild(option);
      });
      if (selectedId && !eligibleUsers.some((u) => String(u.id) === String(selectedId))) {
        const option = document.createElement('option');
        option.value = String(selectedId);
        const fallbackUser = allUsers.find((u) => String(u.id) === String(selectedId));
        option.textContent = fallbackUser ? formatAssignmentOptionLabel(fallbackUser) || `User #${selectedId}` : `User #${selectedId}`;
        technicianAdminUserSelect.appendChild(option);
      }
      if (selectedId) {
        technicianAdminUserSelect.value = String(selectedId);
      } else {
        technicianAdminUserSelect.value = previous && previous !== '' ? previous : '';
      }
    }

    function resetTechnicianForm() {
      technicianEditId = null;
      if (technicianForm) {
        technicianForm.reset();
        technicianForm.dataset.pending = '0';
      }
      if (technicianAdminUserSelect) {
        technicianAdminUserSelect.disabled = false;
        technicianAdminUserSelect.value = '';
      }
      if (technicianActiveInput) technicianActiveInput.checked = true;
      if (technicianDlgTitle) technicianDlgTitle.textContent = 'Add technician';
      if (technicianSubmitBtn) technicianSubmitBtn.textContent = 'Save technician';
    }

  // ---- Region 3 (verbatim lines 6345-7038): bulk view + dialogs + init
    async function openTechnicianDialog(record = null) {
      if (!technicianDlg) {
        // eslint-disable-next-line no-console
        console.warn('technicians: #technicianDlg missing.');
        return;
      }
      resetTechnicianForm();
      await ensureTechnicianUsersLoaded({ force: true });
      populateTechnicianAdminUserOptions(record ? record.admin_user_id : null);

      if (record) {
        technicianEditId = record.id;
        if (technicianDlgTitle) technicianDlgTitle.textContent = 'Edit technician';
        if (technicianSubmitBtn) technicianSubmitBtn.textContent = 'Update technician';
        if (technicianAdminUserSelect) {
          technicianAdminUserSelect.value = String(record.admin_user_id);
          technicianAdminUserSelect.disabled = true;
        }
        if (technicianDisplayNameInput) technicianDisplayNameInput.value = record.display_name || '';
        if (technicianPhoneInput) technicianPhoneInput.value = record.phone || '';
        if (technicianSkillsInput) technicianSkillsInput.value = (record.skills || []).join(', ');
        if (technicianRegionsInput) technicianRegionsInput.value = (record.regions || []).join(', ');
        if (technicianActiveInput) technicianActiveInput.checked = Boolean(record.active);
      }

      openDialog(technicianDlg);
      if (!record && technicianAdminUserSelect) {
        technicianAdminUserSelect.value = '';
      }
      if (technicianDisplayNameInput && !record) {
        setTimeout(() => technicianDisplayNameInput.focus(), 50);
      }
    }

    async function loadTechnicians() {
      if (!techniciansTbody) { console.warn('technicians: #techniciansTbody missing — technicians will not load.'); return; }
      techniciansState.loading = true;
      try {
        const data = await adminAPI.fetch(API.adminTechnicians, { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        techniciansState.items = items;
        updateTechnicianProfiles(items);
        setTechnicianRosterState({ loaded: true, loading: false });
        renderTechnicians(items);
      } catch (err) {
        techniciansState.items = [];
        renderTechnicians([]);
        showFlash(err.message || 'Failed to load technicians', 'error');
      } finally {
        techniciansState.loading = false;
        setTechnicianRosterState({ ...getTechnicianRosterState(), loading: false });
      }
    }

    function getTechnicianStatusInfo(status, isActive = true) {
      const normalized = (status || '').toString().toLowerCase();
      if (normalized === 'injured') {
        return { label: 'Injured', className: 'badge warn' };
      }
      if (normalized === 'inactive' || isActive === false) {
        return { label: 'Unavailable', className: 'badge off' };
      }
      return { label: 'Active', className: 'badge ok' };
    }

    function renderTechnicians(items = []) {
      if (!techniciansTbody) return;
      techniciansTbody.innerHTML = '';
      if (!items.length) {
        techniciansTbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No technicians configured yet.</td></tr>';
        return;
      }
      items.forEach((tech, idx) => {
        const tr = document.createElement('tr');
        tr.dataset.id = String(tech.id);
        const adminLabel = tech.admin_username
          ? `${escapeHtml(tech.admin_username)}${tech.admin_email ? ` (${escapeHtml(tech.admin_email)})` : ''}`
          : `User #${escapeHtml(String(tech.admin_user_id))}`;
        const meta = tech.meta || {};
        const queue = meta.queue || {};
        const throughput = meta.throughput || {};
        const ai = meta.ai_quality || {};
        const completion = meta.completion || {};
        const metaBits = [];
        if (tech.phone) metaBits.push(`Phone ${tech.phone}`);
        if (Array.isArray(tech.regions) && tech.regions.length) metaBits.push(`Regions ${tech.regions.join(', ')}`);
        if (Array.isArray(tech.skills) && tech.skills.length) metaBits.push(`Skills ${tech.skills.join(', ')}`);
        const technicianMeta = metaBits.length
          ? `<div class="tech-roster-meta">${metaBits.map((bit) => `<span>${escapeHtml(bit)}</span>`).join('')}</div>`
          : '';
        const statusInfo = getTechnicianStatusInfo(tech.status, tech.active);
        const statusTitleParts = [];
        if (tech.status_changed_at) statusTitleParts.push(`Updated ${formatDateTimeShort(tech.status_changed_at)}`);
        if (!tech.active && tech.status !== 'inactive') statusTitleParts.push('Profile inactive');
        const statusTitle = statusTitleParts.length ? statusTitleParts.join(' • ') : statusInfo.label;
        const statusButton = `
          <button type="button" class="tech-status-btn" data-tech-board="${tech.id}" title="${escapeHtml(statusTitle)}">
            <span class="${statusInfo.className}">${escapeHtml(statusInfo.label)}</span>
          </button>
        `;

        const queueTotal = Number(queue.total || 0);
        const queueBreakdownMap = [
          ['pending', 'P'],
          ['in_progress', 'IP'],
          ['waiting_customer', 'W'],
          ['escalated', 'Esc'],
          ['parked', 'Park'],
          ['resolved_by_technician', 'Tech'],
        ];
        const queueBits = queueBreakdownMap
          .map(([key, label]) => {
            const value = Number(queue[key] || 0);
            return value ? `${label}:${value}` : '';
          })
          .filter(Boolean);
        const queueBreakdownText = queueBits.length ? queueBits.join(' • ') : 'No active tickets';
        const queueTitleParts = [];
        if (queueBits.length) queueTitleParts.push(queueBits.join(' • '));
        queueTitleParts.push(`Total ${queueTotal}`);
        const queueTitle = queueTitleParts.filter(Boolean).join(' • ') || 'No active tickets';

        const aiScoreRaw = typeof ai.average_score === 'number' ? Number(ai.average_score) : null;
        const aiScoreDisplay = aiScoreRaw != null ? Math.round(aiScoreRaw) : null;
        const lifetimeAverageRaw = typeof ai.lifetime_average_score === 'number' ? Number(ai.lifetime_average_score) : null;
        const lifetimeAverageDisplay = lifetimeAverageRaw != null ? Math.round(lifetimeAverageRaw) : null;
        let qualityClass = 'badge';
        if (aiScoreDisplay != null) {
          if (aiScoreDisplay >= 85) qualityClass = 'badge ok';
          else if (aiScoreDisplay < 70) qualityClass = 'badge warn';
        }
        const qualityTitleParts = [];
        if (aiScoreRaw != null) qualityTitleParts.push(`Average score ${aiScoreRaw}`);
        if (typeof ai.evaluated === 'number') qualityTitleParts.push(`${ai.evaluated} evaluations`);
        if (typeof ai.low_scores === 'number' && ai.low_scores > 0) qualityTitleParts.push(`${ai.low_scores} low scores`);
        if (lifetimeAverageRaw != null) qualityTitleParts.push(`Lifetime avg ${lifetimeAverageRaw}`);
        const recentScoresPreview = Array.isArray(ai.recent_scores)
          ? ai.recent_scores
              .map((entry) => {
                if (!entry) return null;
                const rawValue = typeof entry.score === 'number' ? Number(entry.score) : null;
                if (rawValue == null) return null;
                const flagged = (Array.isArray(entry.flags) && entry.flags.length > 0) || Number(entry.flag_count || 0) > 0;
                return `${Math.round(rawValue)}${flagged ? '⚑' : ''}`;
              })
              .filter(Boolean)
              .slice(0, 3)
          : [];
        if (recentScoresPreview.length) qualityTitleParts.push(`Recent ${recentScoresPreview.join(', ')}`);
        if (ai.last_evaluated_at) qualityTitleParts.push(`Last evaluated ${formatDateTimeShort(ai.last_evaluated_at)}`);
        const qualityTitle = qualityTitleParts.length ? qualityTitleParts.join(' • ') : 'No AI evaluations yet';
        const lifetimeBadgeText = lifetimeAverageDisplay != null ? `L${lifetimeAverageDisplay}` : '';

        const completionSubmissions = Number(completion.submissions || 0);
        const completionClosed = Number(completion.closed_success || 0);
        const completionRatePercent = typeof completion.completion_rate === 'number'
          ? Math.round(Number(completion.completion_rate))
          : null;
        let completionClass = 'badge';
        if (completionRatePercent != null) {
          if (completionRatePercent >= 90) completionClass = 'badge ok';
          else if (completionRatePercent < 60) completionClass = 'badge warn';
        }
        const completionDisplayParts = [];
        if (completionSubmissions > 0) completionDisplayParts.push(`Closed ${completionClosed}/${completionSubmissions}`);
        if (typeof completion.rework === 'number' && completion.rework > 0) completionDisplayParts.push(`Rework ${completion.rework}`);
        const completionDisplayText = completionDisplayParts.length ? completionDisplayParts.join(' • ') : 'No submissions yet';
        const completionTooltipParts = [...completionDisplayParts];
        if (typeof completion.pending_review === 'number' && completion.pending_review > 0) completionTooltipParts.push(`Pending review ${completion.pending_review}`);
        if (typeof completion.awaiting_customer === 'number' && completion.awaiting_customer > 0) completionTooltipParts.push(`Waiting customer ${completion.awaiting_customer}`);
        if (typeof completion.open_after_submission === 'number' && completion.open_after_submission > 0) completionTooltipParts.push(`Open ${completion.open_after_submission}`);
        const completionTooltip = completionTooltipParts.length ? completionTooltipParts.join(' • ') : 'No submissions yet';
        const completionRateDisplay = completionRatePercent != null ? `${completionRatePercent}%` : '—';

        const lastActivityIso = throughput.last_submission_at || tech.updated_at || tech.created_at;
        const lastActivityLabel = lastActivityIso ? formatDateTimeShort(lastActivityIso) : '—';
        const activityParts = [];
        if (lastActivityIso) activityParts.push(formatDateTimeFull(lastActivityIso));
        const throughput7 = throughput.resolved_last_7d ?? throughput.resolved_recent;
        const throughput30 = throughput.resolved_last_30d ?? throughput.resolved_total;
        if (typeof throughput7 === 'number') activityParts.push(`${throughput7} resolved (7d)`);
        if (typeof throughput30 === 'number') activityParts.push(`${throughput30} resolved (30d)`);
        const lastActivityTitle = activityParts.length ? activityParts.join(' • ') : 'No activity recorded';

        tr.innerHTML = `
          <td>${idx + 1}</td>
          <td>
            <strong>${escapeHtml(tech.display_name || '')}</strong>
            ${technicianMeta}
          </td>
          <td>${adminLabel}</td>
          <td>${statusButton}</td>
          <td>
            <div class="tech-queue" title="${escapeHtml(queueTitle)}">
              <strong>${queueTotal}</strong>
              <div class="tech-queue-breakdown">${escapeHtml(queueBreakdownText)}</div>
            </div>
          </td>
          <td><span class="${qualityClass}" title="${escapeHtml(qualityTitle)}">${escapeHtml(aiScoreDisplay != null ? String(aiScoreDisplay) : '—')}${lifetimeBadgeText ? ` <span class="tech-quality-lifetime">${escapeHtml(lifetimeBadgeText)}</span>` : ''}</span></td>
          <td>
            <div class="tech-completion" title="${escapeHtml(completionTooltip)}">
              <span class="${completionClass}">${escapeHtml(completionRateDisplay)}</span>
              <div class="tech-completion-breakdown">${escapeHtml(completionDisplayText)}</div>
            </div>
          </td>
          <td><span title="${escapeHtml(lastActivityTitle)}">${escapeHtml(lastActivityLabel)}</span></td>
          <td>
            <button class="btn" data-tact="edit" data-id="${tech.id}">✏️ Edit</button>
            <button class="btn warn" data-tact="status" data-id="${tech.id}">Status</button>
            <button class="btn ${tech.active ? 'warn' : 'success'}" data-tact="toggle" data-id="${tech.id}" data-current="${tech.active ? '1' : '0'}">${tech.active ? 'Deactivate' : 'Activate'}</button>
          </td>`;
        techniciansTbody.appendChild(tr);
      });
    }

    function findTechnicianByAdminUserId(adminUserId) {
      if (adminUserId == null) return null;
      return techniciansState.items.find((item) => Number(item.admin_user_id) === Number(adminUserId)) || null;
    }

    function resetTechnicianStatusDialog() {
      technicianStatusState.technician = null;
      technicianStatusState.tickets = [];
      technicianStatusState.assignments = {};
      technicianStatusState.loadingTickets = false;
      if (technicianStatusName) technicianStatusName.textContent = '—';
      if (technicianStatusReason) technicianStatusReason.value = '';
      if (technicianStatusSelect) technicianStatusSelect.value = 'injured';
      if (technicianStatusTickets) technicianStatusTickets.innerHTML = '';
      if (technicianStatusTechList) technicianStatusTechList.innerHTML = '';
      if (technicianStatusEmpty) {
        technicianStatusEmpty.style.display = '';
        technicianStatusEmpty.textContent = 'No open tickets assigned to this technician.';
      }
      if (technicianStatusForm) technicianStatusForm.dataset.pending = '0';
      if (technicianStatusSubmit) {
        technicianStatusSubmit.disabled = false;
        technicianStatusSubmit.textContent = 'Save changes';
      }
    }

    function getCurrentTechnicianAssignment(ticket) {
      if (!ticket) return null;
      const ticketId = String(ticket.id);
      if (Object.prototype.hasOwnProperty.call(technicianStatusState.assignments, ticketId)) {
        return technicianStatusState.assignments[ticketId];
      }
      return ticket.assignee_user_id;
    }

    function moveTechnicianTicketAssignment(ticketId, adminUserId) {
      if (!ticketId || adminUserId == null) return;
      const ticket = technicianStatusState.tickets.find((item) => String(item.id) === String(ticketId));
      if (!ticket) return;
      const original = ticket.assignee_user_id;
      const targetId = Number(adminUserId);
      if (Number(original) === targetId) {
        delete technicianStatusState.assignments[String(ticketId)];
      } else {
        technicianStatusState.assignments[String(ticketId)] = targetId;
      }
    }

    function renderTechnicianStatusDialog() {
      if (!technicianStatusTickets || !technicianStatusTechList) return;
      const { technician, tickets, loadingTickets } = technicianStatusState;

      if (technicianStatusName && technician) {
        technicianStatusName.textContent = technician.display_name || 'Technician';
      }

      if (loadingTickets) {
        if (technicianStatusEmpty) {
          technicianStatusEmpty.style.display = '';
          technicianStatusEmpty.textContent = 'Loading tickets…';
        }
        technicianStatusTickets.innerHTML = '';
        technicianStatusTechList.innerHTML = '';
        return;
      }

      if (!tickets.length) {
        if (technicianStatusEmpty) {
          technicianStatusEmpty.style.display = '';
          technicianStatusEmpty.textContent = 'No open tickets assigned to this technician.';
        }
        technicianStatusTickets.innerHTML = '';
      } else {
        if (technicianStatusEmpty) technicianStatusEmpty.style.display = 'none';
        technicianStatusTickets.innerHTML = '';
        tickets.forEach((ticket) => {
          const card = document.createElement('div');
          card.className = 'tech-status-ticket';
          card.draggable = true;
          card.dataset.ticketId = String(ticket.id);
          const currentAssignee = getCurrentTechnicianAssignment(ticket);
          const originalAssignee = ticket.assignee_user_id;
          const targetTech = Number(currentAssignee) === Number(originalAssignee)
            ? technicianStatusState.technician
            : findTechnicianByAdminUserId(currentAssignee);
          if (Number(currentAssignee) !== Number(originalAssignee)) {
            card.classList.add('reassigned');
          }
          const subject = ticket.subject ? escapeHtml(ticket.subject) : `Ticket ${escapeHtml(String(ticket.id)).slice(0, 8)}`;
          const statusLabel = typeof formatTicketStatus === 'function' ? formatTicketStatus(ticket.status) : (ticket.status || '');
          const createdLabel = ticket.created_at ? formatDateTimeShort(ticket.created_at) : 'Unknown';
          const targetLabel = targetTech ? targetTech.display_name : `User ${currentAssignee}`;
          card.innerHTML = `
            <div class="tech-status-ticket-title">${subject}</div>
            <div class="tech-status-ticket-meta">
              <span>Status ${escapeHtml(statusLabel || String(ticket.status || ''))}</span>
              <span>Opened ${escapeHtml(createdLabel)}</span>
              ${ticket.priority ? `<span>Priority ${escapeHtml(ticket.priority)}</span>` : ''}
            </div>
            ${Number(currentAssignee) !== Number(originalAssignee) ? `<div class="tech-status-ticket-target">→ ${escapeHtml(targetLabel || '')}</div>` : ''}
          `;
          card.addEventListener('dragstart', handleTechnicianStatusTicketDragStart);
          card.addEventListener('dragend', handleTechnicianStatusTicketDragEnd);
          technicianStatusTickets.appendChild(card);
        });
      }

      technicianStatusTechList.innerHTML = '';
      if (!technician) return;

      const extractQueueTotal = (record) => {
        const meta = record && record.meta ? record.meta : {};
        const queue = meta.queue || {};
        return Number(queue.total || 0);
      };
      const extractQualityMetrics = (record) => {
        const meta = record && record.meta ? record.meta : {};
        const ai = meta.ai_quality || {};
        return {
          average: typeof ai.average_score === 'number' ? Number(ai.average_score) : null,
          lifetime: typeof ai.lifetime_average_score === 'number' ? Number(ai.lifetime_average_score) : null,
        };
      };

      const dropTargets = [];
      dropTargets.push({
        admin_user_id: technician.admin_user_id,
        display_name: technician.display_name,
        status: technician.status,
        active: technician.active,
        queue: extractQueueTotal(technician),
        quality: extractQualityMetrics(technician),
        isSelf: true,
      });
      techniciansState.items
        .filter((item) => String(item.id) !== String(technician.id) && item.active && item.status === 'active')
        .forEach((item) => {
          dropTargets.push({
            admin_user_id: item.admin_user_id,
            display_name: item.display_name,
            status: item.status,
            active: item.active,
            queue: extractQueueTotal(item),
            quality: extractQualityMetrics(item),
            isSelf: false,
          });
        });

      dropTargets.forEach((target) => {
        const targetAdminId = Number(target.admin_user_id);
        const finalTickets = technicianStatusState.tickets.filter(
          (ticket) => Number(getCurrentTechnicianAssignment(ticket)) === targetAdminId,
        );
        const incomingTickets = technicianStatusState.tickets.filter(
          (ticket) =>
            Number(getCurrentTechnicianAssignment(ticket)) === targetAdminId &&
            Number(ticket.assignee_user_id) !== targetAdminId,
        );
        const statusInfo = getTechnicianStatusInfo(target.status, target.active);
        const queueParts = [`Current queue ${target.queue || 0}`];
        if (incomingTickets.length) queueParts.push(`Incoming +${incomingTickets.length}`);
        const qualityAverage = target.quality && target.quality.average != null ? Math.round(Number(target.quality.average)) : null;
        const qualityLifetime = target.quality && target.quality.lifetime != null ? Math.round(Number(target.quality.lifetime)) : null;
        const qualityLabelParts = [];
        if (qualityAverage != null) qualityLabelParts.push(`Q${qualityAverage}`);
        if (qualityLifetime != null) qualityLabelParts.push(`L${qualityLifetime}`);
        const qualityText = qualityLabelParts.length ? qualityLabelParts.join(' • ') : '';
        const assignedBadge = `<span class="badge">${finalTickets.length} assigned</span>`;
        const assignedListHtml = incomingTickets.length
          ? incomingTickets
              .map((ticket) => {
                const subject = ticket.subject ? ticket.subject : String(ticket.id).slice(0, 8);
                return `<div class="tech-status-assigned-ticket">${escapeHtml(subject)}</div>`;
              })
              .join('')
          : `<div class="tech-status-dropzone-empty">${target.isSelf ? 'Drop here to keep tickets with this technician' : 'Drop tickets here to reassign'}</div>`;

        const zone = document.createElement('div');
        zone.className = 'tech-status-dropzone';
        zone.dataset.adminUserId = String(target.admin_user_id);
        zone.innerHTML = `
          <header>
            <strong>${escapeHtml(target.display_name || 'Technician')}</strong>
            ${assignedBadge}
          </header>
          <div class="tech-status-dropzone-meta">
            <span class="${statusInfo.className}">${escapeHtml(statusInfo.label)}</span>
            <span>${escapeHtml(queueParts.join(' • '))}</span>
            ${qualityText ? `<span>${escapeHtml(qualityText)}</span>` : ''}
          </div>
          <div class="tech-status-assigned">${assignedListHtml}</div>
        `;
        zone.addEventListener('dragover', handleTechnicianStatusDropDragOver);
        zone.addEventListener('dragleave', handleTechnicianStatusDropDragLeave);
        zone.addEventListener('drop', handleTechnicianStatusDrop);
        technicianStatusTechList.appendChild(zone);
      });
    }

    async function openTechnicianStatusDialog(technician) {
      if (!technicianStatusDlg) { console.warn('technicians: #technicianStatusDlg missing.'); return; }
      if (!technician) return;
      resetTechnicianStatusDialog();
      technicianStatusState.technician = technician;
      if (technicianStatusSelect) {
        const preferredStatus = technician.status === 'active' ? 'injured' : (technician.status || 'injured');
        technicianStatusSelect.value = preferredStatus;
      }
      if (technicianStatusName) technicianStatusName.textContent = technician.display_name || 'Technician';
      technicianStatusState.loadingTickets = true;
      renderTechnicianStatusDialog();
      openDialog(technicianStatusDlg);
      setTimeout(() => {
        if (technicianStatusReason) technicianStatusReason.focus();
      }, 60);
      try {
        const data = await adminAPI.fetch(API.adminTechnicianOpenTickets(technician.id), { method: 'GET' });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        technicianStatusState.tickets = items;
      } catch (err) {
        technicianStatusState.tickets = [];
        showFlash(err && err.message ? err.message : 'Failed to load technician queue', 'error');
      } finally {
        technicianStatusState.loadingTickets = false;
        renderTechnicianStatusDialog();
      }
    }

    function closeTechnicianStatusDialog() {
      if (technicianStatusDlg) {
        try { technicianStatusDlg.close(); } catch { technicianStatusDlg.open = false; }
      }
      resetTechnicianStatusDialog();
    }

    function handleTechnicianStatusTicketDragStart(event) {
      const ticketId = event.currentTarget?.dataset?.ticketId;
      if (!ticketId) return;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        try { event.dataTransfer.setData('text/plain', ticketId); } catch {}
      }
      event.currentTarget.classList.add('dragging');
    }

    function handleTechnicianStatusTicketDragEnd(event) {
      event.currentTarget.classList.remove('dragging');
    }

    function handleTechnicianStatusDropDragOver(event) {
      event.preventDefault();
      if (event.currentTarget) event.currentTarget.classList.add('dragover');
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    }

    function handleTechnicianStatusDropDragLeave(event) {
      if (event.currentTarget) event.currentTarget.classList.remove('dragover');
    }

    function handleTechnicianStatusDrop(event) {
      event.preventDefault();
      const zone = event.currentTarget;
      if (!zone) return;
      zone.classList.remove('dragover');
      const adminUserId = zone.dataset.adminUserId;
      if (!adminUserId) return;
      const ticketId = event.dataTransfer ? event.dataTransfer.getData('text/plain') : null;
      if (!ticketId) return;
      moveTechnicianTicketAssignment(ticketId, Number(adminUserId));
      renderTechnicianStatusDialog();
    }

    async function handleTechnicianStatusFormSubmit(event) {
      event.preventDefault();
      if (!technicianStatusForm) return;
      if (technicianStatusForm.dataset.pending === '1') return;
      const technician = technicianStatusState.technician;
      if (!technician) return;
      const selectedStatus = technicianStatusSelect ? technicianStatusSelect.value : 'injured';
      const validStatuses = new Set(['active', 'inactive', 'injured']);
      const statusValue = validStatuses.has(selectedStatus) ? selectedStatus : 'injured';
      const reasonText = technicianStatusReason ? technicianStatusReason.value.trim() : '';
      if (!reasonText) {
        showFlash('Supervisor note is required.', 'error');
        if (technicianStatusReason) technicianStatusReason.focus();
        return;
      }

      const assignmentEntries = Object.entries(technicianStatusState.assignments)
        .map(([ticketId, adminUserId]) => {
          const ticket = technicianStatusState.tickets.find((item) => String(item.id) === String(ticketId));
          if (!ticket) return null;
          if (Number(ticket.assignee_user_id) === Number(adminUserId)) return null;
          return {
            ticket,
            ticketId,
            adminUserId: Number(adminUserId),
          };
        })
        .filter(Boolean);

      technicianStatusForm.dataset.pending = '1';
      if (technicianStatusSubmit) {
        technicianStatusSubmit.disabled = true;
        technicianStatusSubmit.textContent = 'Saving…';
      }

      try {
        await adminAPI.fetch(API.adminTechnicianStatus(technician.id), {
          method: 'POST',
          body: JSON.stringify({
            status: statusValue,
            reason: reasonText,
          }),
        });

        const statusInfo = getTechnicianStatusInfo(statusValue, statusValue === 'active');
        for (const entry of assignmentEntries) {
          const { ticket, adminUserId } = entry;
          const targetTech = findTechnicianByAdminUserId(adminUserId);
          const targetName = targetTech ? targetTech.display_name : `User ${adminUserId}`;
          const baseNote = reasonText.endsWith('.') ? reasonText : `${reasonText}.`;
          const assignmentNote = `${baseNote} Ticket reassigned to ${targetName} while ${technician.display_name} is marked ${statusInfo.label.toLowerCase()}.`;
          await adminAPI.fetch(API.adminTicket(ticket.id), {
            method: 'PATCH',
            body: JSON.stringify({
              assignee_user_id: adminUserId,
              assignment_note: assignmentNote,
            }),
          });
        }

        showFlash(`Status updated for ${technician.display_name}`);
        closeTechnicianStatusDialog();
        await loadTechnicians();
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to update technician status', 'error');
      } finally {
        technicianStatusForm.dataset.pending = '0';
        if (technicianStatusSubmit) {
          technicianStatusSubmit.disabled = false;
          technicianStatusSubmit.textContent = 'Save changes';
        }
      }
    }

    async function handleTechnicianFormSubmit(event) {
      event.preventDefault();
      if (!technicianForm) return;
      if (technicianForm.dataset.pending === '1') return;
      const editing = Boolean(technicianEditId);
      const targetId = technicianEditId;
      if (technicianSubmitBtn) {
        technicianSubmitBtn.disabled = true;
        technicianSubmitBtn.textContent = editing ? 'Saving…' : 'Creating…';
      }
      technicianForm.dataset.pending = '1';

      try {
        const adminUserId = editing ? null : technicianAdminUserSelect?.value;
        if (!editing && !adminUserId) {
          throw new Error('Select an admin user');
        }
        const payload = {
          display_name: technicianDisplayNameInput ? technicianDisplayNameInput.value.trim() : '',
          phone: technicianPhoneInput ? technicianPhoneInput.value.trim() : '',
          skills: parseListInput(technicianSkillsInput ? technicianSkillsInput.value : ''),
          regions: parseListInput(technicianRegionsInput ? technicianRegionsInput.value : ''),
          active: technicianActiveInput ? Boolean(technicianActiveInput.checked) : true,
        };

        if (!payload.display_name) {
          throw new Error('Display name is required');
        }

        if (editing) {
          await adminAPI.fetch(API.adminTechnician(targetId), {
            method: 'PATCH',
            body: JSON.stringify(payload),
          });
        } else {
          await adminAPI.fetch(API.adminTechnicians, {
            method: 'POST',
            body: JSON.stringify({
              ...payload,
              admin_user_id: Number(adminUserId),
            }),
          });
        }
        showFlash(editing ? 'Technician updated' : 'Technician created');
        if (technicianDlg) {
          try { technicianDlg.close(); } catch {}
        }
        resetTechnicianForm();
        await loadTechnicians();
        const tb7 = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.technicianBoard;
        if (tb7) {
          tb7.setStale(true);
          if (tb7.isVisible()) await tb7.refresh({ silent: true });
        }
      } catch (err) {
        let message = err && err.message ? err.message : 'Failed to save technician';
        if (message === 'missing_technician_role') {
          message = 'Selected admin user must have the field technician or supervisor role before creating a technician profile.';
        } else if (message === 'technician_exists') {
          message = 'A technician profile already exists for the selected admin user.';
        }
        showFlash(message, 'error');
      } finally {
        technicianForm.dataset.pending = '0';
        if (technicianSubmitBtn) {
          technicianSubmitBtn.disabled = false;
          technicianSubmitBtn.textContent = editing ? 'Update technician' : 'Save technician';
        }
      }
    }

    async function handleTechnicianTableClick(event) {
      const statusBtn = event.target.closest('button[data-tech-board]');
      if (statusBtn && statusBtn.dataset.techBoard) {
        setTechniciansViewMode('board', { focusTechnicianId: statusBtn.dataset.techBoard });
        return;
      }

      const btn = event.target.closest('button[data-tact]');
      if (!btn) return;
      const id = btn.dataset.id;
      if (!id) return;
      const technician = techniciansState.items.find((item) => String(item.id) === String(id));
      const action = btn.dataset.tact;
      if (action === 'edit') {
        openTechnicianDialog(technician || null);
        return;
      }
      if (action === 'status' && technician) {
        openTechnicianStatusDialog(technician);
        return;
      }
      if (action === 'toggle' && technician) {
        const nextActive = btn.dataset.current !== '1';
        try {
          await adminAPI.fetch(API.adminTechnician(id), {
            method: 'PATCH',
            body: JSON.stringify({ active: nextActive }),
          });
          showFlash(nextActive ? 'Technician activated' : 'Technician deactivated');
          await loadTechnicians();
        } catch (err) {
          let message = err && err.message ? err.message : 'Failed to update technician';
          if (message === 'missing_technician_role') {
            message = 'Assign the field technician role to this user before activating their technician profile.';
          } else if (message === 'technician_has_open_tickets') {
            message = 'This technician still has open tickets. Reassign or close them before deactivating — a deactivated technician has no board lane.';
          }
          showFlash(message, 'error');
        }
      }
    }

    function initTechniciansView() {
      if (!techniciansView) {
        // eslint-disable-next-line no-console
        console.warn('technicians: #techniciansView missing — Tech Support page will not initialize.');
        return;
      }
      if (!techniciansInitialized) {
        techniciansInitialized = true;
        if (addTechnicianBtn) addTechnicianBtn.addEventListener('click', () => { openTechnicianDialog(); });
        if (technicianForm) technicianForm.addEventListener('submit', handleTechnicianFormSubmit);
        if (techniciansTbody) techniciansTbody.addEventListener('click', handleTechnicianTableClick);
        if (technicianStatusForm) technicianStatusForm.addEventListener('submit', handleTechnicianStatusFormSubmit);
        if (technicianStatusCancel) technicianStatusCancel.addEventListener('click', () => { closeTechnicianStatusDialog(); });
        if (technicianStatusClose) technicianStatusClose.addEventListener('click', () => { closeTechnicianStatusDialog(); });
        if (technicianStatusDlg) {
          technicianStatusDlg.addEventListener('cancel', (dlgEvent) => {
            dlgEvent.preventDefault();
            closeTechnicianStatusDialog();
          });
        }
        if (techModeBoardBtn) {
          techModeBoardBtn.addEventListener('click', () => setTechniciansViewMode('board'));
        }
        if (techModeTableBtn) {
          techModeTableBtn.addEventListener('click', () => setTechniciansViewMode('table'));
        }
        // Customer map — visible to the tech-support supervisor (+ root). The
        // /map endpoint grants field_tech_supervisor read-only location access.
        if (techModeMapBtn && hasRole('root', 'field_tech_supervisor')) {
          techModeMapBtn.hidden = false;
          techModeMapBtn.addEventListener('click', () => setTechniciansViewMode('map'));
        }
        if (techCustomerMapExport) {
          techCustomerMapExport.addEventListener('click', () => {
            const a = document.createElement('a');
            a.href = customerMapExportUrl();
            a.download = 'customer_map.csv';
            document.body.appendChild(a); a.click(); a.remove();
          });
        }
      }
      setTechniciansViewMode(techniciansViewMode || 'board');
      loadTechnicians().catch(() => {});
    }

export default {
  init: () => { resolveDom(); initTechniciansView(); },
  setViewMode: (mode, options) => setTechniciansViewMode(mode, options),
  loadTechnicians: () => loadTechnicians(),
  getState: () => techniciansState,
  getViewMode: () => techniciansViewMode,
  getTechnicianStatusInfo,
  findTechnicianByAdminUserId,
  populateAdminUserOptions: populateTechnicianAdminUserOptions,
  userHasTechnicianRole,
};
